import { inject, Injectable } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ExpenseType, TripRequestStatus } from "../../dashboard/models/trip-request-models";
import { futureDateValidator, futuretimeValidator, pastDateValidator, pasttimeValidator } from "../../shared/validators/date-validators";

@Injectable({
  providedIn: 'root',
})
export class TripRequestFormFactory {
    private fb = inject(FormBuilder);

    createPostTripRequestForm() : FormGroup {
      const today = new Date();
      return this.fb.group({
        tripRequest: this.fb.group ({
          tripRequestId: [0],
          tripRequestStatus: [TripRequestStatus.Unsubmitted],
          isSameDriver: [true],
          driverId: ['', [Validators.required]],
          driverFirstName: ['', [Validators.required]],
          driverLastName: ['', [Validators.required]],
          driverMannualEntry:[false],
          saveDriverIntoProfile:[false],
          samePassenger:[true],
          anotherMedicaidConsumer: [false],
          appointmentTypeId:['', [Validators.required]],
          appointmentType: ['', [Validators.required]],
          isRoundTrip: [false],
          showRoundTrip: [true],
          showAdditionalStop: [true],
          tripAuthorize: ['', [Validators.required]],
          isAcceptedPreTrip:[false],
          tripAppointments: this.fb.array([
            this.fb.group({
              appointmentId:[0],
              appointmentDate: [today, [Validators.required, pastDateValidator()]],
              appointmentTime: [today, [Validators.required]],
              driverId:['', [Validators.required]],
              driverFirstName: ['', [Validators.required]],
              driverLastName: ['', [Validators.required]],
              driverMannualEntry:[false],
              saveDriverIntoProfile:[false],
              totalMiles:[0],
              totalMileageAmount:[0],
              expenseAmount:[0],
              showExplanationText:[false],
              explanationText:['', [Validators.required]],
              appointmentPassengers: this.fb.array([
                this.fb.group({
                    passengerId: [0],
                    firstName: ['', [Validators.required]],
                    lastName: ['', [Validators.required]],
                })
              ]),
              tripExpenses: this.fb.array([
                this.fb.group({
                    expenseId: [0],
                    expenseType: [ExpenseType.Toll],
                    expenseAmount: [''],
                }),
                this.fb.group({
                    expenseId: [0],
                    expenseType: [ExpenseType.Parking],
                    expenseAmount: [''],
                }),
              ]),
              tripLegs : this.fb.array([]),
            }, {
              validators: pasttimeValidator
            })
          ]),
          tripPassengers: this.fb.array([
            this.fb.group({
                passengerId: [0],
                firstName: ['', [Validators.required]],
                lastName: ['', [Validators.required]],
            })
          ]),
          tripAddresses : this.fb.array([]),
          appointmentVerificationDocuments: this.fb.group({
            documentUploading: [false],
            documentEmailing: [false],
            documentMailing: [false],
            documentFaxing: [false],
            appointmentDocument: [null, Validators.required]
          }),
          tollDocuments: this.fb.group({
            documentUploading: [false],
            documentEmailing: [false],
            documentMailing: [false],
            documentFaxing: [false],
            tollDocument: [null]
          }),
          parkingDocuments: this.fb.group({
            documentUploading: [false],
            documentEmailing: [false],
            documentMailing: [false],
            documentFaxing: [false],
            parkingDocument: [null]
          })
        }),
      });
    }

    createPreTripRequestForm() : FormGroup {
      const today = new Date();
      return this.fb.group({
        tripRequest: this.fb.group ({
          tripRequestId: [0],
          tripRequestStatus: [TripRequestStatus.Unsubmitted],
          appointmentTypeId:['', [Validators.required]],
          appointmentType: ['', [Validators.required]],
          isRoundTrip: [false],
          showRoundTrip: [true],
          showAdditionalStop: [true],
          tripAuthorize: ['', [Validators.required]],
          tripAppointments: this.fb.array([
            this.fb.group({
              appointmentId:[0],
              appointmentDate: [today, [Validators.required, futureDateValidator()]],
              appointmentTime: [today, [Validators.required]],
              totalMiles:[0],
              totalMileageAmount:[0],
              expenseAmount:[0],
              showExplanationText:[false],
              explanationText:[null],
              tripLegs : this.fb.array([]),
            }, {
              validators: futuretimeValidator
            })
          ]),
          tripAddresses : this.fb.array([]),
        }),
      });
    }

    getPostTripAppointmentFg(): FormGroup {
      const today = new Date();
      return this.fb.group({
        appointmentId: [0],
        appointmentDate: [today, [Validators.required, pastDateValidator()]],
        appointmentTime: [today, [Validators.required]],
        driverId:[''],
        driverFirstName: [''],
        driverLastName: [''],
        driverMannualEntry:[false],
        saveDriverIntoProfile: [false],
        totalMiles:[0],
        totalMileageAmount:[0],
        expenseAmount:[0],
        showExplanationText:[false],
        explanationText:['', [Validators.required]],
        appointmentPassengers: this.fb.array([
          this.fb.group({
            passengerId: [0],
            firstName: ['', [Validators.required]],
            lastName: ['', [Validators.required]],
          }),
        ]),
        tripExpenses: this.fb.array([
            this.fb.group({
                expenseId: [0],
                expenseType: [ExpenseType.Toll],
                expenseAmount: [''],
            }),
            this.fb.group({
                expenseId: [0],
                expenseType: [ExpenseType.Parking],
                expenseAmount: [''],
            }),
        ]),
        tripLegs : this.fb.array([])
      },{
        validators: pasttimeValidator
      });
    }

    getPreTripAppointmentFg(): FormGroup {
      const today = new Date();
      return this.fb.group({
        appointmentId: [0],
        appointmentDate: [today, [Validators.required, futureDateValidator()]],
        appointmentTime: [today, [Validators.required]],
        totalMiles:[0],
        totalMileageAmount:[0],
        expenseAmount:[0],
        showExplanationText:[false],
        explanationText:[null],
        tripLegs : this.fb.array([])
      },{
        validators: futuretimeValidator
      });
    }

    getTripAddressFg(seqNo: number): FormGroup {
      return this.fb.group({
        tripAddressId: [0],
        seqNo: [seqNo],
        addressCategoryId: ['', [Validators.required]],
        providerOrNickName: ['', [Validators.required]],
        otherCategoryType: ['', Validators.required],
        previousAddress: [''],
        address: ['', [Validators.required]],
        saveAddress: [false],
        locationMannualEntry: [false],
        selectedAddress: [''],
        isManualAddress: [false],
        isMapAddress: [false]
      });
    }

    getPassengerFg(): FormGroup {
      return this.fb.group(
            {
                firstName: ['', [Validators.required, Validators.maxLength(50)]],
                lastName: ['', [Validators.required, Validators.maxLength(50)]],
            })
    }

    createAgentTripRequestForm() : FormGroup {
      const today = new Date();
      return this.fb.group({
        tripRequest: this.fb.group ({
          tripRequestId: [0],
          recipientId: [null, [Validators.required,Validators.minLength(10)]],
          tripType:['Post'],
          firstName: [''],
          lastName: [''],
          middleInitial: [''],
          tripRequestStatus: [TripRequestStatus.Unsubmitted],
          isSameDriver: [true],
          driverId: ['', [Validators.required]],
          driverFirstName: ['', [Validators.required]],
          driverLastName: ['', [Validators.required]],
          driverMannualEntry:[false],
          saveDriverIntoProfile:[false],
          samePassenger:[true],
          anotherMedicaidConsumer: [false],
          appointmentTypeId:['', [Validators.required]],
          appointmentType: ['', [Validators.required]],
          isRoundTrip: [false],
          showRoundTrip: [true],
          showAdditionalStop: [true],
          tripAuthorize: ['', [Validators.required]],
          isSameAgentWillReview:[true],
          tripAppointments: this.fb.array([
            this.fb.group({
              appointmentId:[0],
              appointmentDate: [today, [Validators.required, futureDateValidator()]],
              appointmentTime: [today, [Validators.required]],
              driverId:['', [Validators.required]],
              driverFirstName: ['', [Validators.required]],
              driverLastName: ['', [Validators.required]],
              driverMannualEntry:[false],
              saveDriverIntoProfile:[false],
              totalMiles:[0],
              totalMileageAmount:[0],
              expenseAmount:[0],
              showExplanationText:[false],
              explanationText:['', Validators.required],
              appointmentPassengers: this.fb.array([
                this.fb.group({
                    passengerId: [0],
                    firstName: ['', [Validators.required]],
                    lastName: ['', [Validators.required]],
                })
              ]),
              tripExpenses: this.fb.array([
                this.fb.group({
                    expenseId: [0],
                    expenseType: [ExpenseType.Toll],
                    expenseAmount: [''],
                }),
                this.fb.group({
                    expenseId: [0],
                    expenseType: [ExpenseType.Parking],
                    expenseAmount: [''],
                }),
              ]),
              tripLegs : this.fb.array([]),
            }, {
              validators: futuretimeValidator
            })
          ]),
          tripPassengers: this.fb.array([
            this.fb.group({
                passengerId: [0],
                firstName: ['', [Validators.required]],
                lastName: ['', [Validators.required]],
            })
          ]),
          tripAddresses : this.fb.array([]),
          appointmentVerificationDocuments: this.fb.group({
            appointmentDocument: [null]
          }),
          tollDocuments: this.fb.group({
            tollDocument: [null]
          }),
          parkingDocuments: this.fb.group({
            parkingDocument: [null]
          })
        }),
      });
    }

}